import type { PhoneInventory } from '../lib/selfPhoneProduct';

type Props = {
  inventory: PhoneInventory;
  loading?: boolean;
};

const STATUS_LABELS = {
  complete: 'Full',
  open: 'Open now',
  upcoming: 'Upcoming',
} as const;

export default function ShippingBatchSchedule({ inventory, loading }: Props) {
  const batches = inventory.batches ?? [];

  return (
    <div className="space-y-3">
      {batches.map((batch) => {
        const claimedPct =
          batch.size > 0 ? Math.min(100, (batch.sold / batch.size) * 100) : 0;
        const isOpen = batch.status === 'open';

        return (
          <div
            key={batch.number}
            className={`rounded-2xl border p-4 transition-colors ${
              isOpen
                ? 'border-white/25 bg-white/[0.05]'
                : 'border-white/10 bg-white/[0.02]'
            }`}
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-semibold text-white">
                  Batch {batch.number}
                  {isOpen && (
                    <span className="ml-2 text-xs font-medium text-white/60">· current</span>
                  )}
                </p>
                <p className="mt-1 text-sm text-white/55">Ships {batch.shipLabel}</p>
              </div>
              <span
                className={`rounded-full border px-2.5 py-1 text-xs font-medium ${
                  batch.status === 'open'
                    ? 'border-white/20 bg-white/10 text-white'
                    : batch.status === 'complete'
                      ? 'border-white/10 text-white/40'
                      : 'border-white/10 text-white/50'
                }`}
              >
                {loading ? '…' : STATUS_LABELS[batch.status]}
              </span>
            </div>

            <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-white/50">
              <span>
                {batch.status === 'complete'
                  ? `${batch.size} phones claimed`
                  : batch.status === 'open'
                    ? `${batch.remaining} of ${batch.size} spots left`
                    : `${batch.size} phones in batch`}
              </span>
              {batch.status !== 'upcoming' && (
                <span>{batch.sold} claimed</span>
              )}
            </div>

            <div className="mt-2 h-1 overflow-hidden rounded-full bg-white/10">
              <div
                className={`h-full transition-all duration-500 ease-out ${
                  batch.status === 'complete' ? 'bg-white/40' : 'bg-white'
                }`}
                style={{ width: loading ? '0%' : `${claimedPct}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
